// The Documents page's three chart aggregations plus the team → user drill state.
//
// This is the app-side adapter for `@rdrive/plugin-chart-bar-line`' `BaseBarChart`/`BaseLineChart`
// (S11 bit 2): the three SFCs it replaces — `DocumentFileTypeBarChart.vue`,
// `CountCreatedLineChart.vue`, `TeamUserDocumentBarChart.vue` — each aggregated its
// own rows inside a chart component. The aggregation is domain logic and belongs in
// the app (SKILL.md rules 1–3); what is left in the package is a chart that plots
// `[{ label, value }]`.
//
// Pure JS: no store, no i18n, no DOM. Refs, computeds or plain values go in, refs
// and computeds come out, and the page owns the translation of every string that
// reaches the screen.

import { computed, ref, toValue } from 'vue';

// Kept hard-coded and English, exactly as the three SFCs had them, so bit 2 changes
// no rendered text by accident. TRANSLATION DEBT: `@rdrive/vocabulary` (registered at the
// app's root by `createDashboard`, from `dashboard.config.js`) already carries `unknownTeam`/`unknownUser` in every locale (the
// page's own table uses the same two literals), so the fix is to pass the two labels in as
// options — a separate change with its own smoke.
const UNKNOWN_TEAM = 'Unknown Team';
const UNKNOWN_USER = 'Unknown User';

const BYTES_PER_GB = 1024 * 1024 * 1024;
const SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

/**
 * A byte count in GB with two decimals — the uploads chart's formatter, verbatim
 * from `CountCreatedLineChart.vue` (which appended the unit at each call site).
 *
 * @param {number} bytes
 * @returns {string} e.g. `'1.25 GB'`
 */
export function formatSize(bytes) {
  return `${(bytes / BYTES_PER_GB).toFixed(2)} GB`;
}

/**
 * A byte count in whatever unit fits — the file-type chart's formatter, verbatim
 * from `DocumentFileTypeBarChart.vue`.
 *
 * @param {number} bytes
 * @returns {string} e.g. `'4.71 MB'`
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${SIZE_UNITS[i]}`;
}

/**
 * The Documents page's chart data.
 *
 * @param {Object} args
 * @param {import('vue').MaybeRefOrGetter<Array<Object>>} args.documents the REST document list
 * @param {import('vue').MaybeRefOrGetter<Object>} args.teamsObj `store.getters.getTeamsObj`
 * @param {import('vue').MaybeRefOrGetter<Object>} args.usersObj `store.getters.getUsersObj`
 * @returns {{
 *   fileTypeMetric: import('vue').Ref<'count'|'size'>,
 *   fileTypeRows: import('vue').ComputedRef<Array<{ label: string, value: number }>>,
 *   uploadsMetric: import('vue').Ref<'count'|'size'>,
 *   uploadsRows: import('vue').ComputedRef<Array<{ label: string, value: number }>>,
 *   teamUserLevel: import('vue').Ref<'teams'|'users'>,
 *   selectedTeam: import('vue').Ref<string|null>,
 *   teamUserRows: import('vue').ComputedRef<Array<{ id: string, label: string, value: number }>>,
 *   teamUserColorIndex: import('vue').ComputedRef<number>,
 *   selectTeamUser: (row?: { label?: string }) => void,
 *   formatSize: (bytes: number) => string,
 *   formatFileSize: (bytes: number) => string,
 * }}
 */
export function useDocumentCharts({ documents, teamsObj, usersObj }) {
  const docs = () => toValue(documents) ?? [];
  const teams = () => toValue(teamsObj) ?? {};
  const users = () => toValue(usersObj) ?? {};

  // ------------------------------------------------------------- file types
  const fileTypeMetric = ref('count');

  // Folders are excluded the way they always were: a doc with no `fileType` is
  // skipped, and only folders lack one.
  const fileTypeTotals = computed(() => {
    const fileTypes = {};
    docs().forEach((doc) => {
      if (doc.fileType) {
        if (!fileTypes[doc.fileType]) {
          fileTypes[doc.fileType] = { count: 0, size: 0 };
        }
        fileTypes[doc.fileType].count += 1;
        fileTypes[doc.fileType].size += doc.fileSize || 0;
      }
    });
    return Object.entries(fileTypes).sort((a, b) => a[0].localeCompare(b[0]));
  });

  const fileTypeRows = computed(() =>
    fileTypeTotals.value.map(([label, totals]) => ({
      label,
      value: fileTypeMetric.value === 'count' ? totals.count : totals.size,
    })),
  );

  // ------------------------------------------------------------- uploads over time
  const uploadsMetric = ref('count');

  // One point per `createdDate` DAY, ascending, with the value accumulated — the
  // cumulative curve is the page's business, the base chart just plots the rows.
  // Folders are counted here, exactly as before.
  const uploadTotals = computed(() => {
    const dateCountMap = new Map();
    const dateSizeMap = new Map();

    docs().forEach((doc) => {
      const date = new Date(doc.createdDate).toISOString().split('T')[0];
      dateCountMap.set(date, (dateCountMap.get(date) || 0) + 1);
      dateSizeMap.set(date, (dateSizeMap.get(date) || 0) + (doc.fileSize || 0));
    });

    const sortedDates = Array.from(dateCountMap.keys()).sort();
    let cumulativeCount = 0;
    let cumulativeSize = 0;

    return sortedDates.map((date) => {
      cumulativeCount += dateCountMap.get(date);
      cumulativeSize += dateSizeMap.get(date);
      return { label: date, count: cumulativeCount, size: cumulativeSize };
    });
  });

  const uploadsRows = computed(() =>
    uploadTotals.value.map((point) => ({
      label: point.label,
      value: uploadsMetric.value === 'count' ? point.count : point.size,
    })),
  );

  // ------------------------------------------------------------- teams → users
  const teamUserLevel = ref('teams');
  // The team NAME, not its id: the old chart keyed its aggregate by name and looked
  // the drill target up by the clicked bar's category label, so two teams sharing a
  // name merge — kept as-is rather than silently changing what the bars count.
  const selectedTeam = ref(null);

  const teamTotals = computed(() => {
    const teamData = {};

    docs().forEach((doc) => {
      if (doc.createdByTeamId) {
        const teamName = teams()[doc.createdByTeamId]?.name || UNKNOWN_TEAM;
        teamData[teamName] = teamData[teamName] || { total: 0, users: {}, id: doc.createdByTeamId };
        teamData[teamName].total += 1;

        if (doc.createdByUserId) {
          const userName = users()[doc.createdByUserId]?.displayName || UNKNOWN_USER;
          teamData[teamName].users[doc.createdByUserId] =
            teamData[teamName].users[doc.createdByUserId] || { name: userName, value: 0 };
          teamData[teamName].users[doc.createdByUserId].value += 1;
        }
      }
    });

    return Object.entries(teamData).map(([name, data]) => ({
      name,
      value: data.total,
      users: data.users,
      id: data.id,
    }));
  });

  const teamUserRows = computed(() => {
    if (teamUserLevel.value === 'users' && selectedTeam.value) {
      const team = teamTotals.value.find((entry) => entry.name === selectedTeam.value);
      // The old SFC threw here when the selected team disappeared under it (a
      // document reload while drilled in); an empty chart is the better failure.
      if (!team) return [];
      return Object.entries(team.users)
        .map(([userId, user]) => ({ id: userId, label: user.name, value: user.value }))
        .sort((a, b) => a.value - b.value);
    }

    return teamTotals.value
      .map((team) => ({ id: team.id, label: team.name, value: team.value }))
      .sort((a, b) => a.value - b.value);
  });

  // chart.series[0] / [1] are the old '#5470c6' (teams) and '#91cc75' (users)
  const teamUserColorIndex = computed(() => (teamUserLevel.value === 'teams' ? 0 : 1));

  // The SFC's `handleChartClick` toggle: a click at team level drills in, any click
  // at user level comes back out.
  const selectTeamUser = (row) => {
    if (teamUserLevel.value === 'teams') {
      if (!row) return;
      selectedTeam.value = row.label;
      teamUserLevel.value = 'users';
    } else {
      teamUserLevel.value = 'teams';
      selectedTeam.value = null;
    }
  };

  return {
    fileTypeMetric,
    fileTypeRows,
    uploadsMetric,
    uploadsRows,
    teamUserLevel,
    selectedTeam,
    teamUserRows,
    teamUserColorIndex,
    selectTeamUser,
    formatSize,
    formatFileSize,
  };
}
